import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { QueryErrorState } from '../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../components/dashboard/QueryLoadingBlock';
import { api } from '../lib/api';
import { buildOverviewActivity } from '../lib/dashboard/overview/buildOverviewActivity';
import { useQueryRetry } from '../lib/dashboard/useQueryRetry';
import { useOrganisationId } from '../lib/session';
import { ActivityFeed } from './activity/ActivityFeed';
import { ActivitySearch } from './activity/ActivitySearch';

/**
 * Activity — searchable newest-first feed
 * (Ghost Screen Activity.dc.html). Built from live product signals.
 */
export function ActivityPage() {
  const { t, i18n } = useTranslation('overview');
  const organisationId = useOrganisationId();
  const [query, setQuery] = useState('');

  const screensQuery = useQuery({
    queryKey: ['screens', organisationId],
    queryFn: () => api.listScreens(organisationId),
    refetchInterval: 30_000,
  });
  const playlistsQuery = useQuery({
    queryKey: ['playlists', organisationId],
    queryFn: () => api.listPlaylists(organisationId),
  });
  const mediaQuery = useQuery({
    queryKey: ['media-assets', organisationId],
    queryFn: () => api.listMediaAssets(organisationId),
  });
  const schedulesQuery = useQuery({
    queryKey: ['schedules', organisationId],
    queryFn: () => api.listSchedules(organisationId),
  });
  const groupsQuery = useQuery({
    queryKey: ['screen-groups', organisationId],
    queryFn: () => api.listScreenGroups(organisationId),
  });
  const playsQuery = useQuery({
    queryKey: ['playback-plays', organisationId, 'activity'],
    queryFn: () => api.listPlaybackEvents(organisationId, { limit: 100 }),
  });

  const retry = useQueryRetry(async () => {
    await Promise.all([
      screensQuery.refetch(),
      playlistsQuery.refetch(),
      mediaQuery.refetch(),
      schedulesQuery.refetch(),
      groupsQuery.refetch(),
      playsQuery.refetch(),
    ]);
  });

  const items = useMemo(
    () =>
      buildOverviewActivity({
        screens: screensQuery.data ?? [],
        playlists: playlistsQuery.data ?? [],
        playbackEvents: playsQuery.data ?? [],
        mediaAssets: mediaQuery.data ?? [],
        schedules: schedulesQuery.data ?? [],
        groups: groupsQuery.data ?? [],
        limit: 80,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- feed text is translated inside, so re-run on language change
    [
      screensQuery.data,
      playlistsQuery.data,
      playsQuery.data,
      mediaQuery.data,
      schedulesQuery.data,
      groupsQuery.data,
      i18n.language,
    ],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => item.text.toLowerCase().includes(q));
  }, [items, query]);

  const loading =
    screensQuery.isPending ||
    playlistsQuery.isPending ||
    mediaQuery.isPending ||
    schedulesQuery.isPending ||
    groupsQuery.isPending ||
    playsQuery.isPending;
  const failed =
    screensQuery.isError ||
    playlistsQuery.isError ||
    mediaQuery.isError ||
    schedulesQuery.isError ||
    groupsQuery.isError ||
    playsQuery.isError;

  const emptyMessage = query.trim() ? t('activityPage.noMatches') : t('activityPage.empty');

  return (
    <div
      className="flex w-full min-w-0 flex-col gap-4 self-stretch"
      style={{ animation: 'gs-in 260ms ease both', width: '100%' }}
    >
      <ActivitySearch value={query} onChange={setQuery} />

      {failed || retry.isRetrying ? (
        <QueryErrorState
          message={t('activityPage.loadError')}
          onRetry={retry.retry}
          isRetrying={retry.isRetrying}
        />
      ) : loading ? (
        <QueryLoadingBlock />
      ) : (
        <ActivityFeed items={filtered} emptyMessage={emptyMessage} />
      )}
    </div>
  );
}
